import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {NucleotideType} from 'biojs/Util';
import FolderSelector from 'file-browser/FolderSelector';
import {
  PatternMapLayer,
  QueryingModPatternTemplateOligoMap,
} from 'modbio/bulk-edit-mods/BulkEditModsOligoMap';
import BulkEditModsPreviewsListing from 'modbio/bulk-edit-mods/BulkEditModsPreviewsListing';
import {MAX_NUM_TARGET_CHIPS} from 'modbio/bulk-edit-mods/ModPatternConstants';
import ModifiedNucleotidesLegend from 'molbio/monomers/ModifiedNucleotidesLegend';
import {FONT_PROXIMA_13, FONT_PROXIMA_14} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerProps} from 'ui-library/banners/InlineBanner';
import {ButtonSize} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import ToggleGroup from 'ui-library/ToggleGroup';
import {InputChangeEvent, NumericInputChangeEvent} from 'util/Types';

import {SequenceMapPreviewJustify, BulkEditModsResult} from './BulkEditModsConstants';
import {getShouldCloneLabelText} from './BulkEditModsUtil';

export interface ApplyToTargetsPaneProps {
  targetOligoIds: Array<string>;
  templateOligoId: string;
  templateBasesLength: number;
  justify: SequenceMapPreviewJustify;
  onSetJustify: (justify: SequenceMapPreviewJustify) => void;
  customStartPosition: number | null;
  onSetCustomStartPosition: (customStartPosition: number | null) => void;
  debouncedCustomStartPosition: number | null;
  applyBaseModifications: boolean;
  onSetApplyBaseModifications: (applyBaseModifications: boolean) => void;
  overwriteExistingModifications: boolean;
  onSetOverwriteExistingModifications: (overwriteExistingModifications: boolean) => void;
  results: Array<BulkEditModsResult> | null;
  loadingPreview: boolean;
  submitted: boolean;
  nucleotideType: NucleotideType;
  targetOligoNames: Array<string>;
  submissionMessage: InlineBannerProps | null;
  isBulk: boolean;
  shouldClone: boolean;
  setShouldClone: (shouldClone: boolean) => void;
  numSuccessfulResults: number;
  folderId?: string | null;
  setFolderId: (folderId?: string | null) => void;
}

export default function ApplyToTargetsPane({
  targetOligoIds,
  templateOligoId,
  justify,
  onSetJustify,
  customStartPosition,
  onSetCustomStartPosition,
  debouncedCustomStartPosition,
  applyBaseModifications,
  onSetApplyBaseModifications,
  overwriteExistingModifications,
  onSetOverwriteExistingModifications,
  results,
  loadingPreview,
  submitted,
  nucleotideType,
  targetOligoNames,
  submissionMessage,
  templateBasesLength,
  isBulk,
  shouldClone,
  setShouldClone,
  numSuccessfulResults,
  folderId,
  setFolderId,
}: ApplyToTargetsPaneProps): JSX.Element {
  const templateLayers = [PatternMapLayer.AXIS, PatternMapLayer.MODS];
  if (applyBaseModifications) {
    templateLayers.push(PatternMapLayer.BASES);
  }
  return (
    <div
      className={css(styles.paneContainer)}
      {...getTestProps({component: 'ApplyToTargetsPane', element: 'root'})}
    >
      <div className={css(styles.aboveTableSection)}>
        <div className={css(styles.introText)}>
          <span className={css(styles.text)}>
            Adjust the parameters below to specify how the pattern from
          </span>
          <BenchChip id={templateOligoId} size={ChipSize.MINI} isLink={false} />
          <span className={css(styles.text)}>should be applied to</span>
          <ChipList
            itemIds={targetOligoIds.slice(0, Math.min(MAX_NUM_TARGET_CHIPS, targetOligoIds.length))}
            maxChips={MAX_NUM_TARGET_CHIPS}
            chipSize={ChipSize.MINI}
            isVertical={false}
            showChipsAsLinks={false}
          />
          {targetOligoIds.length > MAX_NUM_TARGET_CHIPS && (
            <span className={css(styles.text)}>{`+ ${
              targetOligoIds.length - MAX_NUM_TARGET_CHIPS
            } more`}</span>
          )}
        </div>
        <div className={css(styles.justify)}>
          <div className={css(styles.justifyToggle)}>
            <ToggleGroup
              size={ButtonSize.SMALL}
              selection={justify}
              onChangeSelection={onSetJustify}
              options={[
                {id: SequenceMapPreviewJustify.LEFT, buttonProps: {text: 'Left justify'}},
                {id: SequenceMapPreviewJustify.RIGHT, buttonProps: {text: 'Right justify'}},
                {id: SequenceMapPreviewJustify.CUSTOM, buttonProps: {text: 'Custom start position'}},
              ]}
              disabled={submitted}
            />
          </div>
          {justify === SequenceMapPreviewJustify.CUSTOM && (
            <NumericInput
              className={css(styles.customStartInput)}
              focusOnMount={true}
              value={customStartPosition}
              onChange={(e: NumericInputChangeEvent) => {
                onSetCustomStartPosition(e.target.numericValue);
              }}
              validateInteger={true}
              placeholder="Specify start position"
              min={1}
              isDisabled={submitted}
            />
          )}
        </div>
        <div className={css(styles.optionCheckboxes)}>
          <CheckboxInput
            checked={applyBaseModifications}
            id="applyBaseModifications"
            label="Apply base modifications"
            onChange={(e: InputChangeEvent) => {
              onSetApplyBaseModifications(e.target.checked);
            }}
            helpText="Transfer bases from pattern to target, in addition to sugar and phosphate modifications. Degenerate bases on the pattern are treated as constraints on the target, but will not be applied."
            disabled={submitted}
            dataTestAttrs={{component: 'ApplyToTargetsPane', element: 'input', key: 'applyBaseModifications'}}
          />
          <CheckboxInput
            checked={overwriteExistingModifications || applyBaseModifications}
            id="overwriteExistingModifications"
            label="Overwrite existing modifications"
            onChange={(e: InputChangeEvent) => {
              onSetOverwriteExistingModifications(e.target.checked);
            }}
            disabled={applyBaseModifications || submitted}
            helpText="Overwrite any conflicting modifications on the target oligo."
          />
        </div>
        <div>
          <span className={css(styles.previewText)}>Preview pattern</span>
          {templateOligoId && <BenchChip id={templateOligoId} size={ChipSize.MINI} isLink={false} />}
        </div>
        <div className={`${css(styles.templateMap)} scrollbar-override`}>
          <QueryingModPatternTemplateOligoMap oligoId={templateOligoId} enabledLayers={templateLayers} />
        </div>
        <div className={css(styles.legend)}>
          <ModifiedNucleotidesLegend
            sequenceId={templateOligoId}
            useNetworkQuery={true}
            baseHighlightComponentTitle="Nucleotide targeted by pattern"
            axisHighlightComponentTitle="Nucleotide altered by pattern"
          />
        </div>
      </div>
      {submissionMessage != null && (
        <div className={css(styles.banner)}>
          <InlineBanner
            {...submissionMessage}
            dataTestAttrs={{
              component: 'ApplyToTargetsPane',
              element: 'banner',
            }}
          />
        </div>
      )}
      {loadingPreview || results == null ? (
        <CenteredHorizontalThrobber />
      ) : (
        <>
          <div className={css(styles.table)}>
            <BulkEditModsPreviewsListing
              templateBasesLength={templateBasesLength}
              targetOligoNames={targetOligoNames}
              results={results}
              justify={justify}
              nucleotideType={nucleotideType}
              submitted={submitted}
              customStartPosition={debouncedCustomStartPosition}
              emptyTableText="No target oligo previews to display"
              successfulResultTooltipText="Pattern applied successfully to oligo"
              previewColumnTitle="Bases"
            />
          </div>
          <CheckboxInput
            className={css(styles.checkbox)}
            id="apply-mod-pattern-clone"
            label={getShouldCloneLabelText(isBulk, numSuccessfulResults)}
            checked={shouldClone}
            onChange={(e: InputChangeEvent) => {
              setShouldClone(e.target.checked);
            }}
            disabled={submitted || numSuccessfulResults === 0}
          />
          {shouldClone && (
            <div className={css(styles.folderSelector)}>
              <FieldLabel label={`Folder for new ${pluralize(numSuccessfulResults, 'entity')}`} />
              <FolderSelector
                enableRedesign={true}
                showInventory={true}
                value={folderId}
                onChange={(e: {target: {value: string}}) => {
                  setFolderId(e.target.value);
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  paneContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  legend: {
    marginLeft: -20,
    width: '100%',
    marginTop: -16,
    marginBottom: -16,
  },
  table: {
    display: 'flex',
    flex: 1,
    minHeight: 200,
  },
  text: {
    ...FONT_PROXIMA_13,
    marginRight: 6,
  },
  aboveTableSection: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
  },
  introText: {
    display: 'flex',
    alignItems: 'center',
  },
  sequenceSearchView: {
    display: 'flex',
    flexDirection: 'column',
    width: '95%',
    height: 300,
  },
  optionCheckboxes: {
    display: 'flex',
    gap: 60,
    alignItems: 'center',
    marginTop: 32,
    marginBottom: 24,
  },
  justify: {
    marginTop: 26,
    display: 'flex',
    gap: 24,
  },
  justifyToggle: {
    display: 'flex',
  },
  customStartInput: {
    flex: 1,
    maxWidth: 150,
  },
  previewText: {
    ...FONT_PROXIMA_14,
    marginRight: 6,
  },
  templateMap: {
    overflow: 'auto',
  },
  banner: {
    marginLeft: 16,
    marginBottom: 16,
    minWidth: 550,
    maxWidth: '50%',
  },
  checkbox: {
    padding: 8,
  },
  folderSelector: {
    padding: 8,
    width: '30%',
  },
});
